import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, User, ArrowRight, Search } from 'lucide-react';
import { MOCK_BLOG_POSTS } from '../../mock';

const categories = ['All', 'Maintenance', 'Education', 'Insurance', 'Cleaning', 'Technology'];

const BlogPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [query, setQuery] = useState('');

  const filtered = MOCK_BLOG_POSTS.filter(p => {
    const matchCat = activeCategory === 'All' || p.category === activeCategory;
    const matchQ = !query || p.title.toLowerCase().includes(query.toLowerCase());
    return matchCat && matchQ;
  });

  const featured = filtered.find(p => p.featured);
  const rest = filtered.filter(p => !p.featured);

  return (
    <div>
      <div className="page-header" style={{ paddingTop: '7rem' }}>
        <h1 style={{ color: 'white', fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, marginBottom: '1rem' }}>Roofing Tips & Articles</h1>
        <p style={{ color: '#e6edf5', maxWidth: '480px', margin: '0 auto' }}>
          Expert advice on maintenance, repairs, and everything roofing from our team of specialists.
        </p>
      </div>

      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container-xl">
          {/* Filters */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '3rem' }}>
            <div style={{ position: 'relative', flex: '1 1 260px' }}>
              <Search size={16} style={{ position: 'absolute', left: '0.875rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
              <input className="input-field" style={{ paddingLeft: '2.5rem' }} placeholder="Search articles..." value={query} onChange={e => setQuery(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {categories.map(c => (
                <button key={c} onClick={() => setActiveCategory(c)} style={{
                  padding: '0.5rem 1rem', borderRadius: '9999px', fontSize: '0.85rem', fontWeight: 500, border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                  background: activeCategory === c ? '#1493e6' : 'var(--bg-secondary)',
                  color: activeCategory === c ? 'white' : 'var(--text-secondary)',
                }}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Featured */}
          {featured && (
            <Link to={`/blog/${featured.slug}`} style={{ textDecoration: 'none', display: 'block', marginBottom: '3rem' }}>
              <div className="card card-hover" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '0', padding: '0', overflow: 'hidden' }}>
                <img src={featured.image} alt={featured.title} style={{ width: '100%', height: '320px', objectFit: 'cover' }} onError={e => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&auto=format&fit=crop&q=80'; }} />
                <div style={{ padding: '2.5rem' }}>
                  <span className="badge badge-accent" style={{ marginBottom: '1rem' }}>Featured</span>
                  <h2 style={{ fontWeight: 800, fontSize: '1.4rem', color: 'var(--text-primary)', marginBottom: '0.75rem', lineHeight: 1.3 }}>{featured.title}</h2>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, fontSize: '0.9rem', marginBottom: '1.5rem' }}>{featured.excerpt}</p>
                  <div style={{ display: 'flex', gap: '1.25rem', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}><User size={13} /> {featured.author}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Clock size={13} /> {featured.readTime} min read</span>
                  </div>
                  <span style={{ color: '#1493e6', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.9rem' }}>
                    Read Article <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            </Link>
          )}

          {/* Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {rest.map(post => (
              <Link key={post.id} to={`/blog/${post.slug}`} style={{ textDecoration: 'none' }}>
                <div className="card card-hover" style={{ padding: '0', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <img src={post.image} alt={post.title} style={{ width: '100%', height: '200px', objectFit: 'cover' }} onError={e => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&auto=format&fit=crop&q=80'; }} />
                  <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <span className="badge badge-info" style={{ marginBottom: '0.75rem', alignSelf: 'flex-start' }}>{post.category}</span>
                    <h3 style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '0.5rem', lineHeight: 1.4 }}>{post.title}</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1.6, flex: 1, marginBottom: '1rem' }}>{post.excerpt}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Clock size={12} /> {post.readTime} min</span>
                      <span style={{ color: '#1493e6', fontWeight: 600 }}>Read →</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)' }}>
              No articles found. Try a different search.
            </div>
          )}
        </div>
      </section>
      <style>{`@media(max-width:768px){div[style*="grid-template-columns: 1.5fr 1fr"]{grid-template-columns:1fr!important}img[style*="height: 320px"]{height:200px!important}}`}</style>
    </div>
  );
};

export default BlogPage;
